import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Trash2, X } from "lucide-react";

const OrderSummery = ({ quotationTemplate, properties, companyId, onOrderChange, selectedAssets, setSelectedAssets,setShowOrderSummaryModal, hideRemove = false }) => {
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    asset: null
  });
  // Load persisted data if available
  useEffect(() => {
    if (quotationTemplate?.content) {
      try {
        const parsedContent = typeof quotationTemplate.content === 'string' 
          ? JSON.parse(quotationTemplate.content)
          : quotationTemplate.content;

        // First check in ShopStep which is the primary location for selected assets
        if (parsedContent.wizardSteps) {
          const shopStep = parsedContent.wizardSteps.find(step => step.id === "ShopStep");
          if (shopStep && shopStep.selectedAssets && shopStep.selectedAssets.length > 0) {
            setSelectedAssets(shopStep.selectedAssets);
            return;
          }
        }
        
        // Fallback to order summary if needed
        if (parsedContent.orderSummary?.selectedAssets && parsedContent.orderSummary.selectedAssets.length > 0) {
          setSelectedAssets(parsedContent.orderSummary.selectedAssets);
        }
      } catch (error) {
        console.error('Error parsing persisted order data:', error);
      }
    }
  }, [quotationTemplate, setSelectedAssets]);

  const formatNumber = (value) => {
    return parseFloat(value).toFixed(2);
  };

  const handleRemoveClick = (asset) => {
    setConfirmDialog({
      show: true,
      asset: asset
    });
  };
  const handleConfirmRemove = () => {
    if (confirmDialog.asset) {
      const updatedSelectedAssets = selectedAssets.filter((a) => a.Id !== confirmDialog.asset.Id);
      setSelectedAssets(updatedSelectedAssets);
      onOrderChange(updatedSelectedAssets); // This will trigger saving to the server in RequestWizard
      // If no assets left, close the order summary modal
      if (updatedSelectedAssets.length === 0 && setShowOrderSummaryModal) {
        setShowOrderSummaryModal(false);
      }
    }
    setConfirmDialog({ show: false, asset: null });
  };

  const handleCancelRemove = () => {
    setConfirmDialog({ show: false, asset: null });
  };

  const calculateTotals = () => {
    const subtotal = selectedAssets.reduce(
      (acc, asset) => acc + (asset.Cost || 0),
      0
    );
    const vat = selectedAssets.reduce(
      (acc, asset) => acc + (asset.GST || 0) + (asset.TransportGST || 0),
      0
    );
    const transportTotal = selectedAssets.reduce(
      (acc, asset) => acc + (asset.TransportPrice || 0),
      0
    );
    const transportGST = selectedAssets.reduce(
      (acc, asset) => acc + (asset.TransportGST || 0),
      0
    );
    const total = subtotal + vat + transportTotal;
    return { subtotal, vat, transportTotal, transportGST, total };
  };

  if (!quotationTemplate?.wizardSteps || selectedAssets.length === 0) {
    return null;
  }

  const { subtotal, vat, transportTotal, transportGST, total } = calculateTotals();

  return (
    <div className="bg-white rounded-lg shadow-md mb-6">
      <div className="flex-1">
        <div className="p-4 border-b">
          <p className="text-sm text-green-600 mb-1">(Tax Invoice will be provided.)</p>
          <h2 className="text-xl font-semibold text-blue-600">Order Summary</h2>
        </div>
        <div className="w-full px-6 py-4">
          <div className="product-list w-full mb-6">
            <ul className="flex flex-col space-y-4">
              {selectedAssets.map((asset, index) => (
                <React.Fragment key={index}>
                  <li className="group">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="text-[15px] text-gray-800">
                          {asset.Code} - {asset.Name}
                          <sup className="text-[13px] text-gray-600 ml-2">
                            x{asset.Units} {asset.PriceUnit === "Day" ? "Day(s)" : "Hours"}
                          </sup>
                        </h4>
                        {asset.startDate && (
                          <p className="text-sm text-gray-600">
                            {moment(asset.startDate).format("LL")} - {moment(asset.endDate).format("LL")}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        {!hideRemove && (
                          <button
                            onClick={() => handleRemoveClick(asset)}
                            className="flex items-center gap-1 px-2 py-1 text-sm font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 hover:border-red-300 transition-all"
                          >
                            <Trash2 className="h-4 w-4" />
                            Remove
                          </button>
                        )}
                        <div className="min-w-[100px] text-right">
                          <span className="text-[15px] font-medium">
                            ${formatNumber(asset.Cost || 0)}
                          </span>
                          <p className="text-sm text-gray-600">
                            (${formatNumber(asset.GST || 0)} GST)
                          </p>
                        </div>
                      </div>
                    </div>
                  </li>
                  {asset.TransportPrice > 0 && (
                    <li>
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="text-[15px] text-gray-800">
                            {asset.Name} Transport
                          </h4>
                        </div>
                        <div className="min-w-[100px] text-right">
                          <span className="text-[15px] font-medium">
                            ${formatNumber(asset.TransportPrice)}
                          </span>
                          <p className="text-sm text-gray-600">
                            (${formatNumber(asset.TransportGST || 0)} GST)
                          </p>
                        </div>
                      </div>
                    </li>
                  )}
                </React.Fragment>
              ))}
            </ul>
          </div>
          
          <div className="w-full h-px bg-gray-200 mb-4"></div>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <p className="text-sm font-medium text-gray-600 uppercase">
                Subtotal
              </p>
              <p className="text-sm font-medium">
                ${formatNumber(subtotal)}
              </p>
            </div>
            {transportTotal > 0 && (
              <>
                <div className="flex justify-between">
                  <p className="text-sm font-medium text-gray-600 uppercase">
                    Transport
                  </p>
                  <p className="text-sm font-medium">
                    ${formatNumber(transportTotal)}
                  </p>
                </div>
                {/* {transportGST > 0 && (
                  <div className="flex justify-between">
                    <p className="text-sm font-medium text-gray-600 uppercase">
                      Transport GST
                    </p>
                    <p className="text-sm font-medium">
                      ${formatNumber(transportGST)}
                    </p>
                  </div>
                )} */}
              </>
            )}
            {vat > 0 && (
              <div className="flex justify-between">
                <p className="text-sm font-medium text-gray-600 uppercase">
                  GST
                </p>
                <p className="text-sm font-medium">
                  ${formatNumber(vat)}
                </p>
              </div>
            )}
            <div className="pt-3 border-t">
              <div className="flex justify-between">
                <p className="text-lg font-semibold text-gray-900">
                  Total
                </p>
                <p className="text-lg font-semibold text-blue-600">
                  ${formatNumber(total)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {confirmDialog.show && !hideRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full m-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Confirm Removal</h3>
              <button
                onClick={handleCancelRemove}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600">
                Are you sure you want to remove{" "}
                <span className="font-semibold">{confirmDialog.asset?.Name}</span> from your order?
              </p>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                onClick={handleCancelRemove}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRemove}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

OrderSummery.propTypes = {
  quotationTemplate: PropTypes.shape({
    wizardSteps: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        fields: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            value: PropTypes.any,
            options: PropTypes.arrayOf(
              PropTypes.shape({
                label: PropTypes.string.isRequired,
                properties: PropTypes.array,
              })
            ),
          })
        ),
      })
    ),
    content: PropTypes.string,
  }).isRequired,
  properties: PropTypes.array.isRequired,
  companyId: PropTypes.string.isRequired,
  onOrderChange: PropTypes.func.isRequired,
  selectedAssets: PropTypes.array.isRequired,
  setSelectedAssets: PropTypes.func.isRequired,
  hideRemove: PropTypes.bool,
};

export default OrderSummery;